import { tr } from '../locales/tr';
import { en } from '../locales/en';
import { ar } from '../locales/ar';
import { de } from '../locales/de';
import { es } from '../locales/es';
import { fr } from '../locales/fr';
import { it } from '../locales/it';
import { ja } from '../locales/ja';
import { ru } from '../locales/ru';
import { zh } from '../locales/zh';
import { DEFAULT_LANGUAGE, type SupportedLanguage } from '../utils/language.utils';

type Translations = Record<string, any>;

const translations: Record<SupportedLanguage, Translations> = {
  tr,
  en,
  ar,
  de,
  es,
  fr,
  it,
  ja,
  ru,
  zh,
};

const ERP_APP_CODES = [
  'crm',
  'accounting',
  'inventory',
  'purchase',
  'sales',
  'manufacturing',
  'hr',
  'project',
  'timesheet',
  'documents',
  'pos',
  'rental',
  'dashboard',
] as const;

const PANEL_NAMES: Record<SupportedLanguage, Record<string, string>> = {
  tr: {
    crm: 'CRM',
    accounting: 'Muhasebe',
    inventory: 'Ürün ve Stok Yönetimi',
    purchase: 'Satin Alma',
    sales: 'Satis',
    manufacturing: 'Uretim',
    hr: 'IK',
    project: 'Proje Yonetimi',
    timesheet: 'Calisma Cizelgeleri',
    documents: 'Belgeler',
    pos: 'Satis Noktasi',
    rental: 'Kiralama',
    dashboard: 'Rapor Paneli',
  },
  en: {
    crm: 'CRM',
    accounting: 'Accounting',
    inventory: 'Inventory',
    purchase: 'Purchase',
    sales: 'Sales',
    manufacturing: 'Manufacturing',
    hr: 'HR',
    project: 'Project Management',
    timesheet: 'Timesheets',
    documents: 'Documents',
    pos: 'Point of Sale',
    rental: 'Rental',
    dashboard: 'Reports',
  },
  ar: {
    crm: 'CRM',
    accounting: 'Accounting',
    inventory: 'Inventory',
    purchase: 'Purchase',
    sales: 'Sales',
    manufacturing: 'Manufacturing',
    hr: 'HR',
    project: 'Project',
    timesheet: 'Timesheet',
    documents: 'Documents',
    pos: 'POS',
    rental: 'Rental',
    dashboard: 'Reports',
  },
  de: {
    crm: 'CRM',
    accounting: 'Buchhaltung',
    inventory: 'Inventar',
    purchase: 'Einkauf',
    sales: 'Verkauf',
    manufacturing: 'Produktion',
    hr: 'HR',
    project: 'Projektmanagement',
    timesheet: 'Zeiterfassung',
    documents: 'Dokumente',
    pos: 'POS',
    rental: 'Vermietung',
    dashboard: 'Berichte',
  },
  fr: {
    crm: 'CRM',
    accounting: 'Comptabilite',
    inventory: 'Inventaire',
    purchase: 'Achats',
    sales: 'Ventes',
    manufacturing: 'Production',
    hr: 'RH',
    project: 'Gestion de projet',
    timesheet: 'Feuilles de temps',
    documents: 'Documents',
    pos: 'Point de vente',
    rental: 'Location',
    dashboard: 'Rapports',
  },
  es: {
    crm: 'CRM',
    accounting: 'Contabilidad',
    inventory: 'Inventario',
    purchase: 'Compras',
    sales: 'Ventas',
    manufacturing: 'Produccion',
    hr: 'RRHH',
    project: 'Gestion de proyectos',
    timesheet: 'Partes de horas',
    documents: 'Documentos',
    pos: 'Punto de venta',
    rental: 'Alquiler',
    dashboard: 'Informes',
  },
  it: {
    crm: 'CRM',
    accounting: 'Contabilita',
    inventory: 'Inventario',
    purchase: 'Acquisti',
    sales: 'Vendite',
    manufacturing: 'Produzione',
    hr: 'HR',
    project: 'Gestione progetti',
    timesheet: 'Timesheet',
    documents: 'Documenti',
    pos: 'Punto vendita',
    rental: 'Noleggio',
    dashboard: 'Report',
  },
  ru: {
    crm: 'CRM',
    accounting: 'Bukhgalteriya',
    inventory: 'Sklad',
    purchase: 'Zakupki',
    sales: 'Prodazhi',
    manufacturing: 'Proizvodstvo',
    hr: 'HR',
    project: 'Upravlenie proektami',
    timesheet: 'Tabeli',
    documents: 'Dokumenty',
    pos: 'POS',
    rental: 'Arenda',
    dashboard: 'Otchety',
  },
  zh: {
    crm: 'CRM',
    accounting: 'Accounting',
    inventory: 'Inventory',
    purchase: 'Purchase',
    sales: 'Sales',
    manufacturing: 'Manufacturing',
    hr: 'HR',
    project: 'Project',
    timesheet: 'Timesheet',
    documents: 'Documents',
    pos: 'POS',
    rental: 'Rental',
    dashboard: 'Reports',
  },
  ja: {
    crm: 'CRM',
    accounting: 'Accounting',
    inventory: 'Inventory',
    purchase: 'Purchase',
    sales: 'Sales',
    manufacturing: 'Manufacturing',
    hr: 'HR',
    project: 'Project',
    timesheet: 'Timesheet',
    documents: 'Documents',
    pos: 'POS',
    rental: 'Rental',
    dashboard: 'Reports',
  },
};

const PANEL_LABELS: Record<SupportedLanguage, { appsLabel: string; settingsLabel: string; searchPlaceholder: string }> = {
  tr: { appsLabel: 'Uygulamalar', settingsLabel: 'Ayarlar', searchPlaceholder: 'Uygulama ara...' },
  en: { appsLabel: 'Applications', settingsLabel: 'Settings', searchPlaceholder: 'Search apps...' },
  ar: { appsLabel: 'Applications', settingsLabel: 'Settings', searchPlaceholder: 'Search apps...' },
  de: { appsLabel: 'Anwendungen', settingsLabel: 'Einstellungen', searchPlaceholder: 'Apps suchen...' },
  fr: { appsLabel: 'Applications', settingsLabel: 'Parametres', searchPlaceholder: 'Rechercher des apps...' },
  es: { appsLabel: 'Aplicaciones', settingsLabel: 'Configuracion', searchPlaceholder: 'Buscar aplicaciones...' },
  it: { appsLabel: 'Applicazioni', settingsLabel: 'Impostazioni', searchPlaceholder: 'Cerca applicazioni...' },
  ru: { appsLabel: 'Prilozheniya', settingsLabel: 'Nastroiki', searchPlaceholder: 'Poisk prilozheniy...' },
  zh: { appsLabel: 'Applications', settingsLabel: 'Settings', searchPlaceholder: 'Search apps...' },
  ja: { appsLabel: 'Applications', settingsLabel: 'Settings', searchPlaceholder: 'Search apps...' },
};

const EXTRA_PORTAL_APP_NAMES: Record<SupportedLanguage, Record<string, string>> = {
  tr: { ai: 'Yapay Zeka', support: 'Destek', knowledge: 'Bilgi Bankasi', signature: 'Imza', studio: 'Studyo', chat: 'Sohbet', field: 'Saha Hizmeti', planning: 'Planlama', helpdesk: 'Yardim Masasi', website: 'Web Sitesi', email: 'E-posta Pazarlama' },
  en: { ai: 'AI', support: 'Support', knowledge: 'Knowledge Base', signature: 'Signature', studio: 'Studio', chat: 'Chat', field: 'Field Service', planning: 'Planning', helpdesk: 'Helpdesk', website: 'Website', email: 'Email Marketing' },
  ar: { ai: 'AI', support: 'Support', knowledge: 'Knowledge Base', signature: 'Signature', studio: 'Studio', chat: 'Chat', field: 'Field Service', planning: 'Planning', helpdesk: 'Helpdesk', website: 'Website', email: 'Email Marketing' },
  de: { ai: 'KI', support: 'Support', knowledge: 'Wissensdatenbank', signature: 'Signatur', studio: 'Studio', chat: 'Chat', field: 'Aussendienst', planning: 'Planung', helpdesk: 'Helpdesk', website: 'Website', email: 'E-Mail Marketing' },
  fr: { ai: 'IA', support: 'Support', knowledge: 'Base de connaissance', signature: 'Signature', studio: 'Studio', chat: 'Chat', field: 'Service terrain', planning: 'Planification', helpdesk: 'Helpdesk', website: 'Site web', email: 'Email marketing' },
  es: { ai: 'IA', support: 'Soporte', knowledge: 'Base de conocimiento', signature: 'Firma', studio: 'Studio', chat: 'Chat', field: 'Servicio de campo', planning: 'Planificacion', helpdesk: 'Helpdesk', website: 'Sitio web', email: 'Email marketing' },
  it: { ai: 'AI', support: 'Supporto', knowledge: 'Knowledge base', signature: 'Firma', studio: 'Studio', chat: 'Chat', field: 'Servizio sul campo', planning: 'Pianificazione', helpdesk: 'Helpdesk', website: 'Sito web', email: 'Email marketing' },
  ru: { ai: 'AI', support: 'Support', knowledge: 'Baza znaniy', signature: 'Podpis', studio: 'Studio', chat: 'Chat', field: 'Polevaya sluzhba', planning: 'Planirovanie', helpdesk: 'Helpdesk', website: 'Veb-sayt', email: 'Email marketing' },
  zh: { ai: 'AI', support: 'Support', knowledge: 'Knowledge Base', signature: 'Signature', studio: 'Studio', chat: 'Chat', field: 'Field Service', planning: 'Planning', helpdesk: 'Helpdesk', website: 'Website', email: 'Email Marketing' },
  ja: { ai: 'AI', support: 'Support', knowledge: 'Knowledge Base', signature: 'Signature', studio: 'Studio', chat: 'Chat', field: 'Field Service', planning: 'Planning', helpdesk: 'Helpdesk', website: 'Website', email: 'Email Marketing' },
};

const GENERIC_PORTAL_DESCRIPTION: Record<SupportedLanguage, string> = {
  tr: 'Bu modul su anda gelistirme asamasindadir.',
  en: 'This module is currently under development.',
  ar: 'This module is currently under development.',
  de: 'Dieses Modul befindet sich derzeit in Entwicklung.',
  fr: 'Ce module est en cours de developpement.',
  es: 'Este modulo se encuentra en desarrollo.',
  it: 'Questo modulo e in fase di sviluppo.',
  ru: 'Etot modul nahoditsya v razrabotke.',
  zh: 'This module is currently under development.',
  ja: 'This module is currently under development.',
};

function buildTranslationWithFallback(language: SupportedLanguage, t: Translations): Translations {
  const panelNames = PANEL_NAMES[language] || PANEL_NAMES.en;
  const panelLabels = PANEL_LABELS[language] || PANEL_LABELS.en;
  const extraPortalNames = EXTRA_PORTAL_APP_NAMES[language] || EXTRA_PORTAL_APP_NAMES.en;
  const portalDesc = GENERIC_PORTAL_DESCRIPTION[language] || GENERIC_PORTAL_DESCRIPTION.en;

  const existingPanelApps = (t as any)?.panel?.apps || {};
  const panelAppsFallback = ERP_APP_CODES.reduce<Record<string, { name: string }>>((acc, code) => {
    acc[code] = { name: panelNames[code] || code };
    return acc;
  }, {});

  const panelApps = {
    ...panelAppsFallback,
    ...existingPanelApps,
  };

  const existingPortalApps = (t as any)?.portal?.apps || {};
  const portalFromPanel = Object.keys(panelApps).reduce<Record<string, { name: string; description: string }>>((acc, code) => {
    const name = panelApps[code]?.name || panelNames[code] || code;
    acc[code] = { name, description: portalDesc };
    return acc;
  }, {});

  const portalFromExtra = Object.keys(extraPortalNames).reduce<Record<string, { name: string; description: string }>>((acc, code) => {
    const name = extraPortalNames[code] || code;
    acc[code] = { name, description: portalDesc };
    return acc;
  }, {});

  const portalApps = {
    ...portalFromPanel,
    ...portalFromExtra,
    ...existingPortalApps,
  };

  return {
    ...t,
    panel: {
      appsLabel: (t as any)?.panel?.appsLabel || panelLabels.appsLabel,
      settingsLabel: (t as any)?.panel?.settingsLabel || panelLabels.settingsLabel,
      apps: panelApps,
    },
    portal: {
      ...(t as any)?.portal,
      searchPlaceholder: (t as any)?.portal?.searchPlaceholder || panelLabels.searchPlaceholder,
      apps: portalApps,
    },
  };
}

export function useTranslation(language: SupportedLanguage = DEFAULT_LANGUAGE) {
  const selected = translations[language] || translations[DEFAULT_LANGUAGE];
  return buildTranslationWithFallback(language, selected);
}

